import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import anime from 'animejs';
import PhoneMock from './PhoneMock';

/**
 * Landing hero. Layered ambient orbs (framer parallax), grid-mask bg,
 * anime.js orbital nodes floating in the top-right, and staggered heading
 * word reveal. All animations no-op under prefers-reduced-motion.
 */
export default function Hero() {
  const orbitRef = useRef<SVGSVGElement | null>(null);
  const reduce = useReducedMotion();
  const [wordIdx, setWordIdx] = useState(0);

  useEffect(() => {
    if (reduce || !orbitRef.current) return;
    const svg = orbitRef.current;
    const rings = svg.querySelectorAll<SVGCircleElement>('[data-ring]');
    const nodes = svg.querySelectorAll<SVGGElement>('[data-node]');

    // Rings breathe in scale — subtle radar pulse.
    anime({
      targets: rings,
      scale: [{ value: 1, duration: 0 }, { value: 1.08, duration: 2200 }, { value: 1, duration: 2200 }],
      opacity: [{ value: 0.55, duration: 0 }, { value: 0.9, duration: 2200 }, { value: 0.55, duration: 2200 }],
      delay: anime.stagger(300),
      easing: 'easeInOutSine',
      loop: true,
    });

    // Nodes orbit the center by animating the parent <g> rotation.
    nodes.forEach((n, i) => {
      anime({
        targets: n,
        rotate: [`0deg`, `360deg`],
        duration: 14000 + i * 4000,
        easing: 'linear',
        loop: true,
      });
    });
    return () => anime.remove([...rings, ...nodes]);
  }, [reduce]);

  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => setWordIdx((i) => (i + 1) % 4), 2400);
    return () => clearInterval(id);
  }, [reduce]);

  // Hero prefix stays put; the last word rotates on a 2.4s cadence.
  // Reduced-motion collapses to a static first word.
  const prefix = 'The internet, on your'.split(' ');
  const rotators = ['terms.', 'privacy.', 'speed.', 'freedom.'];

  return (
    <section className="relative overflow-hidden pt-24 pb-16 sm:pt-32 sm:pb-24 md:pt-36 md:pb-32">
      <div aria-hidden className="pointer-events-none absolute inset-0 grid-bg" />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-40 w-[620px] h-[620px] rounded-full bg-[color:var(--color-brand)]/25 blur-[140px]"
        animate={reduce ? undefined : { x: [0, 30, 0], y: [0, -20, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full bg-[color:var(--color-brand-deep)]/25 blur-[140px]"
        animate={reduce ? undefined : { x: [0, -20, 0], y: [0, 20, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-5 grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-12 items-center min-h-[440px] sm:min-h-[520px]">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="chip mb-6"
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--color-brand-glow)] animate-pulse" />
            Zero logs · REALITY · Post-quantum
          </motion.div>

          <h1 className="h-title text-[2.5rem] sm:text-6xl md:text-7xl font-extrabold leading-[1.05] sm:leading-[1.02]">
            {prefix.map((w, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 24, filter: 'blur(6px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{ duration: 0.55, delay: 0.05 + i * 0.06, ease: [0.2, 0.7, 0.35, 1] }}
                className="inline-block mr-2.5"
              >
                {w}
              </motion.span>
            ))}
            <span
              className="relative inline-block align-baseline overflow-hidden pb-[0.18em] -mb-[0.38em]"
              // Reserve width for the widest rotator so the layout never jumps
              // as words swap in. Chosen from the longest string ("freedom.").
              // pb/-mb pair extends the clip window below the baseline so glyph
              // descenders (p in "speed.") aren't sheared off, without moving layout.
              style={{ minWidth: '5.2ch' }}
              aria-live="polite"
            >
              <AnimatePresence mode="wait">
                <motion.span
                  key={rotators[wordIdx]}
                  initial={{ y: '110%', opacity: 0, filter: 'blur(6px)' }}
                  animate={{ y: '0%', opacity: 1, filter: 'blur(0px)' }}
                  exit={{ y: '-110%', opacity: 0, filter: 'blur(6px)' }}
                  transition={{ duration: 0.55, ease: [0.22, 0.7, 0.36, 1] }}
                  className="inline-block bg-clip-text text-transparent bg-gradient-to-r from-[color:var(--color-brand)] via-[color:var(--color-brand-glow)] to-[color:var(--color-brand-deep)]"
                >
                  {rotators[wordIdx]}
                </motion.span>
              </AnimatePresence>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.45 }}
            className="mt-5 sm:mt-6 max-w-xl text-base sm:text-lg text-[color:var(--color-ink-dim)] leading-relaxed"
          >
            Vpnee is a zero-log Xray REALITY tunnel wrapped in post-quantum VLESS encryption. Looks like a normal HTTPS handshake to censors. Feels like the internet you remember.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.55 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <Link to="/download" className="btn btn-primary">Get Vpnee — free</Link>
            <Link to="/features" className="btn btn-ghost">See features</Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.75 }}
            className="mt-10 grid grid-cols-3 gap-5 max-w-md text-sm"
          >
            <div><div className="h-title text-2xl">0</div><div className="text-[color:var(--color-ink-faint)] text-xs uppercase tracking-widest">Logs kept</div></div>
            <div><div className="h-title text-2xl">1400</div><div className="text-[color:var(--color-ink-faint)] text-xs uppercase tracking-widest">MTU tuned</div></div>
            <div><div className="h-title text-2xl">&lt;15s</div><div className="text-[color:var(--color-ink-faint)] text-xs uppercase tracking-widest">To first byte</div></div>
          </motion.div>
        </div>

        <div className="relative hidden lg:block">
          {/* Orbital radar behind the phone — pure decoration. */}
          <svg ref={orbitRef} viewBox="0 0 400 400" className="absolute inset-0 w-full h-full pointer-events-none">
            {[190, 150, 110, 70].map((r, i) => (
              <circle key={r} data-ring cx="200" cy="200" r={r} fill="none" stroke="rgba(232,71,106,0.28)" strokeWidth={i === 0 ? 1 : 0.6} strokeDasharray={i % 2 ? '4 6' : '0'} style={{ transformOrigin: '200px 200px' }} />
            ))}
            {[
              { r: 110, color: '#ffb0c1' },
              { r: 150, color: '#e8476a' },
              { r: 190, color: '#7ce7c7' },
            ].map((o, i) => (
              <g key={i} data-node style={{ transformOrigin: '200px 200px' }}>
                <circle cx={200 + o.r} cy="200" r="5" fill={o.color} />
                <circle cx={200 + o.r} cy="200" r="10" fill={o.color} opacity="0.15" />
              </g>
            ))}
          </svg>
          <motion.div
            initial={{ opacity: 0, y: 24, rotateY: -12 }}
            animate={{ opacity: 1, y: 0, rotateY: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 0.6, 0.36, 1] }}
            className="relative z-10"
            style={{ perspective: 1200 }}
          >
            <PhoneMock state="connected" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
